// src/components/companies/useCompanies.js
import { useState, useEffect } from 'react';
import api from '../../api';
import { useAuth } from '../../authContext';

export const useCompanies = () => {
  const { token, role } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Daten abrufen ---
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Unternehmen. Bitte versuche es später erneut.');
      console.error('Fehler:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchCompanies();
  }, [token]);


  const toggleCompanyField = async (companyId, field, currentValue) => {
    if (role !== 'Admin' && field === 'active') {
      throw new Error('Nur Admins können den Status ändern.');
    }
    try {
      // Fallunterscheidung für die URL und den Query-Parameter
      let url;
      switch (field) {
        case 'active':
          url = `/companies/${companyId}/active?isActive=${!currentValue}`;
          break;
        case 'highlighted':
          url = `/companies/${companyId}/highlighted?isHighlighted=${!currentValue}`;
          break;
        case 'contacted':
          url = `/companies/${companyId}/contacted?isContacted=${!currentValue}`;
          break;
        case 'verified':
          url = `/companies/${companyId}/verified?isVerified=${!currentValue}`;
          break;
        case 'isExtern':
          url = `/companies/${companyId}/is-extern?isExtern=${!currentValue}`;
          break;
        default:
          // Fallback für zukünftige Felder (falls nötig)
          url = `/companies/${companyId}/${field}?is${field.charAt(0).toUpperCase() + field.slice(1)}=${!currentValue}`;
      }

      await api.put(
        url,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCompanies(
        companies.map((company) =>
          company.id === companyId ? { ...company, [field]: !currentValue } : company
        )
      );
    } catch (err) {
      console.error(`Fehler beim Ändern von ${field}:`, err);
      throw err;
    }
  };


  const addCompany = async (companyData) => {
    try {
      const response = await api.post(
        '/companies',
        {
          ...companyData,
          active: true,
          verified: companyData.verified || false,  // ✅ Wert aus companyData oder false
          isExtern: companyData.isExtern || false,    // ✅ Wert aus companyData oder false
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanies([...companies, {
        ...companyData,
        id: response.data.id,
        active: true,
        verified: companyData.verified || false,  // ✅ Wert aus companyData oder false
        isExtern: companyData.isExtern || false,    // ✅ Wert aus companyData oder false
        contacted: false
      }]);
      return response.data;
    } catch (err) {
      console.error('Fehler beim Erstellen des Unternehmens:', err);
      throw err;
    }
  };

  const editCompany = async (companyId, companyData) => {
    try {
      await api.put(
        `/companies/${companyId}`,
        { ...companyData, updatedAt: new Date().toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanies(
        companies.map((company) =>
          company.id === companyId ? { ...companyData, id: companyId } : company
        )
      );
    } catch (err) {
      console.error('Fehler beim Bearbeiten des Unternehmens:', err);
      throw err;
    }
  };

  const deactivateCompany = async (companyId) => {
    try {
      await api.put(
        `/companies/${companyId}/active?isActive=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanies(
        companies.map((company) =>
          company.id === companyId ? { ...company, active: false } : company
        )
      );
    } catch (err) {
      console.error('Fehler beim Deaktivieren des Unternehmens:', err);
      throw err;
    }
  };

  return {
    companies,
    loading,
    error,
    fetchCompanies,
    toggleCompanyField,
    addCompany,
    editCompany,
    deactivateCompany,
  };
};

export default useCompanies;